import { useNavigate } from 'react-router-dom';
import { GitBranch } from 'lucide-react';
import type { JourneySummary } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { Card } from '../../shared/ui/Card.js';
import { MiniBar } from '../../shared/ui/MiniBar.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { Table, type Column } from '../../shared/ui/Table.js';
import { useJourneys } from '../../shared/api/queries.js';
import { useActiveProject } from '../../shared/api/useActiveProject.js';

/** Journeys are listed, never created: their structure comes from measurement. */
export function JourneyListPage() {
  const navigate = useNavigate();
  const { project } = useActiveProject();
  const { data: journeys = [], isLoading } = useJourneys(project?.id);

  const columns: Array<Column<JourneySummary>> = [
    {
      key: 'name',
      header: 'Parcours',
      render: (row) => (
        <div className="cell-primary">
          <span className="cell-primary__icon">
            <GitBranch size={14} />
          </span>
          <div>
            <div className="cell-primary__label">{row.name}</div>
            <div className="cell-primary__sub">
              {row.stepCount} {row.stepCount > 1 ? 'pages' : 'page'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'sessions',
      header: 'Usagers',
      align: 'end',
      render: (row) => row.sessionCount.toLocaleString('fr-FR'),
    },
    {
      key: 'completion',
      header: 'Vont au bout',
      render: (row) =>
        // A single measured page has no "end" to reach: the rate would always read 100 %.
        row.stepCount <= 1 ? (
          <span className="cell-muted" title="Parcours d'une seule page : pas de fin à atteindre">
            —
          </span>
        ) : (
          <div className="cell-progress">
            <MiniBar value={row.completionRate} tone={row.completionRate < 0.5 ? 'negative' : 'teal'} />
            <span>{Math.round(row.completionRate * 100)}%</span>
          </div>
        ),
    },
    {
      key: 'guidances',
      header: 'Guidances',
      align: 'end',
      render: (row) =>
        row.guidanceCount > 0 ? (
          <Badge tone="brand">{row.guidanceCount}</Badge>
        ) : (
          <span className="cell-muted">—</span>
        ),
    },
    {
      key: 'tracked',
      header: 'Surveillance',
      render: (row) =>
        row.tracked ? (
          <Badge tone="positive">Surveillé</Badge>
        ) : (
          <span className="cell-muted" title="Ouvrez le parcours pour le marquer comme surveillé en priorité">
            —
          </span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Parcours utilisateur"
        description="Chaque parcours est une suite de pages que les usagers traversent sur le portail, reconstituée automatiquement à partir de la mesure. Ouvrez un parcours pour lire où les usagers s'arrêtent et décider des guidances."
      />

      <Card padded={false}>
        <Table
          columns={columns}
          rows={journeys}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/journeys/${row.id}`)}
          empty={
            isLoading
              ? 'Chargement…'
              : "Aucun parcours pour l'instant. Les parcours apparaîtront dès que la mesure aura collecté des sessions."
          }
        />
      </Card>
    </>
  );
}
